import { legacySlice, type LegacyState } from "./legacySlice";

/** Il minimo che serve dello store Redux: cosi' il tipo non dipende dagli altri reducer (api, slice futuri). */
export interface ReduxStoreLike {
  getState(): { legacy: LegacyState };
  dispatch(action: { type: string; payload?: unknown }): unknown;
  subscribe(listener: () => void): () => void;
}

type Getter = (state: LegacyState, getters: Record<string, unknown>) => unknown;
type ActionContext = {
  readonly state: LegacyState;
  getters: Record<string, unknown>;
  commit: LegacyAdapter["commit"];
  dispatch: LegacyAdapter["dispatch"];
};
type Action = (context: ActionContext, payload: unknown) => unknown;

export interface LegacyDefinition {
  state: LegacyState;
  getters: Record<string, Getter>;
  mutations: Record<string, (state: LegacyState, payload: unknown) => void>;
  actions: Record<string, Action>;
}

export interface LegacyAdapter {
  readonly state: LegacyState;
  getters: Record<string, unknown>;
  commit(type: string, payload?: unknown): void;
  dispatch(type: string, payload?: unknown): Promise<unknown>;
  subscribe(listener: () => void): () => void;
  getVersion(): unknown;
}

/**
 * Stessa interfaccia dello store Vuex-like di prima (state/getters/commit/dispatch/subscribe/getVersion),
 * ma la verita' vive in Redux: commit dispatcha l'azione dello slice legacy, i getter leggono
 * lo stato Redux. Le viste con emulatore non vedono differenze. Sparisce in fase 8.
 */
export function createLegacyAdapter(reduxStore: ReduxStoreLike, definition: LegacyDefinition): LegacyAdapter {
  const getters: Record<string, unknown> = {};
  const adapter: LegacyAdapter = {
    get state() {
      return reduxStore.getState().legacy;
    },
    getters,
    commit(type, payload) {
      const action = (
        legacySlice.actions as Record<
          string,
          ((payload: unknown) => { type: string; payload: unknown }) | undefined
        >
      )[type];
      if (!action) throw new Error(`Unknown store mutation: ${type}`);
      reduxStore.dispatch(action(payload));
    },
    dispatch(type, payload) {
      const action = definition.actions[type];
      if (!action) return Promise.reject(new Error(`Unknown store action: ${type}`));
      // Getter vivo su adapter.state: un'azione async che legge context.state dopo un await
      // vede lo stato Redux aggiornato, non uno snapshot preso al momento della chiamata.
      return Promise.resolve(
        action(
          {
            get state() {
              return adapter.state;
            },
            getters,
            commit: adapter.commit,
            dispatch: adapter.dispatch,
          },
          payload,
        ),
      );
    },
    // Notifica solo sui commit allo slice legacy: altre azioni (es. quelle interne di RTK Query
    // sull'api slice) attraversano comunque lo store condiviso ma non devono svegliare le viste legacy.
    subscribe(listener) {
      let previous = reduxStore.getState().legacy;
      return reduxStore.subscribe(() => {
        const current = reduxStore.getState().legacy;
        if (current !== previous) {
          previous = current;
          listener();
        }
      });
    },
    // NON e' esattamente la "versione" di prima: Immer restituisce lo stesso riferimento quando un
    // commit scrive un valore identico, quindi un commit a valore uguale non cambia la versione e non
    // notifica (differenza voluta e innocua: le viste legacy clonano l'oggetto e lo ricommittano).
    getVersion() {
      return reduxStore.getState().legacy;
    },
  };
  Object.entries(definition.getters).forEach(([name, getter]) => {
    Object.defineProperty(getters, name, { enumerable: true, get: () => getter(adapter.state, getters) });
  });
  return adapter;
}
